import type { ToBeFixed } from '@cutr/constants/utility-types';

/**
 * Pick a list of properties from an object
 * into a new object
 */
export const pick = <T extends object, TKeys extends keyof T = keyof T>(
  obj: T | null | undefined,
  keys: TKeys[]
): Pick<T, TKeys> => {
  if (!obj) return {} as Pick<T, TKeys>;
  if (!keys || keys.length === 0) return {} as Pick<T, TKeys>;
  return keys.reduce(
    (acc, key) => {
      if (Object.prototype.hasOwnProperty.call(obj, key)) acc[key] = obj[key];
      return acc;
    },
    {} as Pick<T, TKeys>
  );
};

/**
 * Omit a list of properties from an object
 * returning a new object with the properties
 * that remain
 */
export const omit = <T, TKeys extends keyof T>(
  obj: T,
  keys: TKeys[]
): Omit<T, TKeys> => {
  if (!obj) return {} as Omit<T, TKeys>;
  if (!keys || keys.length === 0) return obj as Omit<T, TKeys>;
  return keys.reduce(
    (acc, key) => {
      // Gross, I know, it's mutating the object, but we
      // are allowing it in this very limited scope due
      // to the performance implications of an omit func.
      // Not a pattern or practice to use elsewhere.
      delete acc[key];
      return acc;
    },
    { ...obj }
  );
};

/**
 * Get a random item from an array
 */
export const sample = <T>(arr: T[]): T =>
  arr[Math.floor(Math.random() * arr.length)];

/**
 * Get item that appears most from an array
 */
export const mostCommonItem = <T>(arr: T[]): T | undefined =>
  [...arr]
    .sort(
      (a, b) =>
        arr.filter((it) => it === a).length -
        arr.filter((it) => it === b).length
    )
    .pop();
/**
 * Humanize a string
 * @example
 * humanize('helloWorld') // Hello World
 */
export const humanize = (str?: string | null) => {
  if (!str) return '';
  return str
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/[_-]+/g, ' ') // Replace underscores and hyphens with space
    .replace(/^./, (s) => s.toUpperCase()) // Capitalize the first letter
    .trim(); // Trim any leading/trailing spaces
};

/**
 * Sleep for a given number of milliseconds
 */
export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Chunk an array into smaller arrays
 */
export const chunk = <T>(arr: T[], size: number): T[][] =>
  arr.reduce(
    (acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]),
    [] as T[][]
  );

/**
 * Check if a value is empty (similar to lodash isEmpty)
 * - null/undefined -> true
 * - false -> false
 * - numbers -> false
 * - empty string -> true
 * - empty array -> true
 * - empty object -> true
 * - empty Map/Set -> true
 */
export const isEmpty = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string' || Array.isArray(value))
    return value.length === 0;
  if (value instanceof Map || value instanceof Set) return value.size === 0;
  if (typeof value === 'object')
    return Object.keys(value as object).length === 0;
  return false;
};

export function debounce<F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  waitFor: number
): (...args: Parameters<F>) => void {
  let timeout: ReturnType<typeof setTimeout>;

  return function debounced(...args: Parameters<F>): void {
    // @ts-expect-error i will not type this :/
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const callerThis = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(callerThis, args), waitFor);
  };
}

export function groupBy<T, K extends keyof ToBeFixed>(
  arr: T[],
  key: (i: T) => K
) {
  return arr.reduce(
    (groups, item) => {
      (groups[key(item)] ||= []).push(item);
      return groups;
    },
    {} as Record<K, T[]>
  );
}

export const escapeHtml = (unsafe: string) =>
  unsafe
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

/**
 * Check if all properties in the pattern object exist and match in the target object.
 * The target object can have additional properties not present in the pattern.
 * Uses strict equality (===) for comparison.
 *
 * @example
 * matches({ a: 1 }, { a: 1, b: 2, c: 3 }) // true
 * matches({ a: 1, b: 2 }, { a: 1, b: 2, c: 3 }) // true
 * matches({ a: 1 }, { a: 2, b: 2 }) // false
 * matches({ a: 1, d: 4 }, { a: 1, b: 2 }) // false
 */
export const matches = <T extends object>(
  pattern: T | null | undefined,
  target: T | null | undefined
): boolean => {
  if (!pattern) return true;
  if (!target) return false;

  return Object.keys(pattern).every(
    (key) => pattern[key as keyof T] === (target as T)[key as keyof T]
  );
};

/**
 * Check if two objects are the same
 * @example
 * isSameObject({ a: 1, b: 'test' }, { a: 1, b: 'test' }) // true
 * isSameObject({ a: 1, b: 2 }, { a: 1, b: 3 }) // false
 * isSameObject({ a: 1 }, { a: 1, b: 2 }) // false
 */

export const isSameObject = (
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>
) => {
  let isSame: boolean = true;

  if (Object.keys(obj1).length !== Object.keys(obj2).length) return false;

  for (const key of Object.keys(obj1)) {
    const a = obj1[key];
    const b = obj2[key];
    if (JSON.stringify(a) !== JSON.stringify(b)) {
      isSame = false;
      break;
    }
  }

  return isSame;
};

export const setDocumentTitle = (title: string) => {
  document.title = ['Cutr', title].filter(Boolean).join(' | ');
};
